import { useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { featureAccess } from "../../permissions/access";
import { WastePercentReportParams } from "../../types/waste-percent-report.type";
import { todayInBangkok } from "../../utils/date";
import { useUserRole } from "../../utils/hooks/useUserRole";
import WastePercentFilters from "./WastePercentFilters";
import WastePercentTable from "./WastePercentTable";
import { API_MONTH_FORMAT, WASTE_PERCENT_REPORT_TITLE } from "./constants";
import { useWastePercentReport } from "./useWastePercentReport";

const PICK_BRANCH_TEXT = "เลือกสาขาเพื่อดูรายงาน";
const LOAD_FAILED_TEXT = "โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
const NO_DATA_TEXT = "ไม่มีข้อมูลในเดือนที่เลือก";

/**
 * สรุปเปอร์เซ็นต์ของขึ้น ของเสีย — the client's monthly Excel sheet of one route (or every route
 * of the branch) read straight off the reconciles: per product and day what went out, came back,
 * was wasted and sold, and the waste percentage. Every figure comes computed from the server.
 *
 * Head office and master pick the branch; everyone else is pinned to their own by the server.
 */
export default function WastePercentReport() {
  const role = useUserRole();
  const canPickBranch = featureAccess.wastePercentReportAllBranches(role);
  // This month on the Bangkok calendar until another is picked.
  const [month, setMonth] = useState<Dayjs>(() => todayInBangkok());
  const [branchCode, setBranchCode] = useState<string | null>(null);
  const [routeUuid, setRouteUuid] = useState<string | null>(null);

  const monthParam = month.format(API_MONTH_FORMAT);
  const isBranchInScope = !canPickBranch || branchCode !== null;
  const params = useMemo<WastePercentReportParams | null>(
    () =>
      isBranchInScope
        ? {
            month: monthParam,
            branch_code: branchCode ?? undefined,
            route_uuid: routeUuid ?? undefined,
          }
        : null,
    [isBranchInScope, monthParam, branchCode, routeUuid],
  );
  const { report, isLoading, isFailed } = useWastePercentReport(params);
  const isEmpty = report !== null && report.categories.length === 0;

  // A route belongs to one branch: one left picked from the previous branch would be refused.
  const handleSelectBranch = (nextBranchCode: string) => {
    setBranchCode(nextBranchCode);
    setRouteUuid(null);
  };

  return (
    <Stack sx={{ py: 1 }}>
      <Typography variant="h6" mb={1}>
        {WASTE_PERCENT_REPORT_TITLE}
      </Typography>
      <WastePercentFilters
        month={month}
        onChangeMonth={setMonth}
        canPickBranch={canPickBranch}
        branchCode={branchCode}
        onSelectBranch={handleSelectBranch}
        routeUuid={routeUuid}
        onSelectRoute={setRouteUuid}
      />
      {!isBranchInScope && <Alert severity="info">{PICK_BRANCH_TEXT}</Alert>}
      {isLoading && (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      )}
      {isFailed && <Alert severity="error">{LOAD_FAILED_TEXT}</Alert>}
      {isEmpty && <Alert severity="info">{NO_DATA_TEXT}</Alert>}
      {report && !isEmpty && <WastePercentTable report={report} />}
    </Stack>
  );
}
